import * as THREE from 'three';
import { GeometryUtils } from '../../../utils/geometry/GeometryUtils';

const AXES = ['x', 'y', 'z'];
const AXIS_VECTORS = {
  x: new THREE.Vector3(1, 0, 0),
  y: new THREE.Vector3(0, 1, 0),
  z: new THREE.Vector3(0, 0, 1),
};

/**
 * Trong khoảng này (ms) tin bbox khung đã cache mà không kiểm tra lại. Hết hạn
 * thì so "chữ ký" của module (rẻ); chỉ khi chữ ký đổi mới clone tính lại (đắt).
 */
const FRAME_BOX_TTL_MS = 100;
const frameBoxCache = new WeakMap();

/**
 * Chữ ký thay đổi khi hình dạng bên trong module đổi: thêm/bớt con, đổi
 * transform của con (co giãn theo công thức), đổi geometry hoặc sửa đỉnh.
 * Không phụ thuộc transform của chính group (bbox là hệ local).
 */
export const frameSignature = group => {
  let hash = 0;
  let count = 0;
  const mix = value => {
    hash = (Math.imul(hash, 31) + value) | 0;
  };
  group.traverse(o => {
    if (o === group) return;
    count += 1;
    const e = o.matrix.elements;
    for (let i = 0; i < 16; i += 1) mix(Math.round(e[i] * 1e5));
    if (o.geometry) {
      mix(o.geometry.id);
      mix(o.geometry.attributes?.position?.version ?? 0);
    }
  });
  return `${count}:${hash}`;
};

/**
 * Anchor key có dạng "<x>X_<y>Y_<z>Z", mỗi phần là min | max | c (tâm).
 * Ví dụ: "minX_maxY_cZ".
 */
export const buildAnchorKey = ({ x = 'c', y = 'c', z = 'c' }) =>
  `${x}X_${y}Y_${z}Z`;

export const parseAnchorKey = key => {
  if (typeof key !== 'string') return null;
  const parts = key.split('_');
  if (parts.length !== 3) return null;
  const result = {};
  for (let i = 0; i < 3; i += 1) {
    const axis = AXES[i];
    const match = parts[i].match(/^(min|max|c)([XYZ])$/);
    if (!match || match[2].toLowerCase() !== axis) return null;
    result[axis] = match[1];
  }
  return result;
};

/**
 * Từ object bất kỳ (mesh được raycast, group…) đi lên tới group module
 * có `userData.instanceId`.
 */
export const findModuleGroup = object => {
  let current = object;
  while (current) {
    if (current.userData?.instanceId) return current;
    current = current.parent;
  }
  return null;
};

/**
 * Bbox "khung" của module trong hệ toạ độ LOCAL của group (bỏ cánh, tay nắm,
 * phụ kiện… nhờ GeometryUtils.moduleOnlyFrame). Không dùng bbox world để tránh
 * sai số khi module xoay.
 *
 * moduleOnlyFrame clone cả group nên kết quả được cache ngắn hạn.
 */
export const computeLocalFrameBox = (
  group,
  frameFn = GeometryUtils.moduleOnlyFrame
) => {
  if (!group) return null;
  const frame = frameFn(group);
  // Đưa bản clone về hệ local: bỏ transform của chính group.
  frame.position.set(0, 0, 0);
  frame.quaternion.identity();
  frame.scale.set(1, 1, 1);
  frame.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(frame);
  return box.isEmpty() ? null : box;
};

export const getLocalFrameBox = (group, opts = {}) => {
  if (!group) return null;
  const { now = Date.now(), ttl = FRAME_BOX_TTL_MS, frameFn } = opts;
  const cached = frameBoxCache.get(group);
  if (cached && now - cached.time < ttl) return cached.box;
  const signature = frameSignature(group);
  if (cached && cached.signature === signature) {
    cached.time = now;
    return cached.box;
  }
  const box = computeLocalFrameBox(group, frameFn);
  if (box) frameBoxCache.set(group, { box, time: now, signature });
  return box ?? cached?.box ?? null;
};

export const invalidateFrameBox = group => {
  if (group) frameBoxCache.delete(group);
};

export const anchorLocalPoint = (box, key) => {
  const parsed = parseAnchorKey(key);
  if (!box || !parsed) return null;
  const point = new THREE.Vector3();
  AXES.forEach(axis => {
    if (parsed[axis] === 'min') point[axis] = box.min[axis];
    else if (parsed[axis] === 'max') point[axis] = box.max[axis];
    else point[axis] = (box.min[axis] + box.max[axis]) / 2;
  });
  return point;
};

export const anchorWorldPoint = (group, box, key) => {
  const local = anchorLocalPoint(box, key);
  if (!local || !group) return null;
  group.updateWorldMatrix(true, false);
  return local.applyMatrix4(group.matrixWorld);
};

/**
 * Toạ độ local -> toạ độ tương đối [u,v,w] theo bbox khung (0 = min, 1 = max;
 * có thể <0 hoặc >1 với điểm nằm ngoài khung như mặt cánh, mặt đá).
 * Trục có kích thước ~0 thì lưu độ lệch tuyệt đối so với min.
 */
export const localToRel = (box, local) =>
  ['x', 'y', 'z'].map(axis => {
    const size = box.max[axis] - box.min[axis];
    const d = local[axis] - box.min[axis];
    return size > 1e-6 ? d / size : d;
  });

export const relToLocal = (box, rel) => {
  const out = new THREE.Vector3();
  ['x', 'y', 'z'].forEach((axis, i) => {
    const size = box.max[axis] - box.min[axis];
    out[axis] = box.min[axis] + (size > 1e-6 ? rel[i] * size : rel[i]);
  });
  return out;
};

/** Anchor có cần bbox khung để giải ra toạ độ không (key, rel: có; local: không). */
export const anchorNeedsBox = anchor => !Array.isArray(anchor?.local);

/**
 * Điểm neo -> toạ độ world. Anchor có ba dạng:
 *  - { instanceId, key }   : điểm kỹ thuật trên bbox khung (góc / giữa cạnh / tâm)
 *  - { instanceId, rel }   : điểm bất kỳ, theo tỉ lệ [u,v,w] của bbox khung, nên
 *                            vẫn bám đúng chỗ khi module đổi kích thước
 *  - { instanceId, local } : điểm cố định [x,y,z] trong hệ local (dạng cũ)
 */
export const resolveAnchorWorld = (group, box, anchor) => {
  if (!group || !anchor) return null;
  if (Array.isArray(anchor.rel) && anchor.rel.length === 3) {
    if (!box) return null;
    group.updateWorldMatrix(true, false);
    return relToLocal(box, anchor.rel).applyMatrix4(group.matrixWorld);
  }
  if (Array.isArray(anchor.local) && anchor.local.length === 3) {
    group.updateWorldMatrix(true, false);
    return new THREE.Vector3()
      .fromArray(anchor.local)
      .applyMatrix4(group.matrixWorld);
  }
  return anchorWorldPoint(group, box, anchor.key);
};

/** Hướng local (mảng [x,y,z]) -> vector đơn vị trong world. */
export const localDirToWorld = (group, dirLocal) => {
  group.updateWorldMatrix(true, false);
  return new THREE.Vector3()
    .fromArray(dirLocal)
    .transformDirection(group.matrixWorld);
};

/** Hướng world -> mảng local [x,y,z] đơn vị trong hệ của group. */
export const worldDirToLocal = (group, dirWorld) => {
  group.updateWorldMatrix(true, false);
  const inverse = group.matrixWorld.clone().invert();
  return dirWorld.clone().transformDirection(inverse).toArray();
};

/** Vector đơn vị (world) của một trục local của group. */
export const localAxisWorldDir = (group, axis) => {
  group.updateWorldMatrix(true, false);
  return AXIS_VECTORS[axis].clone().transformDirection(group.matrixWorld);
};
